import styles from './ls-cta.module.scss';

/**
 * На каком фоне стоит кнопка.
 *
 * `accent` — на белом полотне: фирменная заливка, белая подпись.
 * `inverse` — на фирменной плашке: белая заливка, фирменная подпись.
 * Третьего вида нет и не должно появиться: два фона — две кнопки.
 */
export type CtaTone = 'accent' | 'inverse';

/**
 * Размер кнопки.
 *
 * `md` — обычный, одна кнопка на экране. `sm` — для строки, где кнопок
 * две: в неё обычные просто не помещаются, и это не подгонка на месте,
 * а второй стандарт, одинаковый на всех экранах.
 */
export type CtaSize = 'md' | 'sm';

interface Props {
  label: string;
  onClick: () => void;
  tone?: CtaTone;
  size?: CtaSize;
  /** Круглая кнопка-стрелка рядом. Ведёт туда же, куда и подпись. */
  withArrow?: boolean;
  /** Растянуть по ширине родителя — кнопка под карточкой витрины. */
  isWide?: boolean;
  isDisabled?: boolean;
}

/**
 * Главное действие экрана.
 *
 * Высота, скругление и поля берутся из набора мер заказчика, а не задаются
 * на месте. Человек видит кнопки с разных экранов подряд, и расхождение
 * в несколько пикселей читается как небрежность, даже если он не сможет
 * объяснить, что именно не так.
 *
 * Стрелка — часть того же действия, а не вторая кнопка. Для читалки экрана
 * она спрятана: иначе прозвучала бы одна и та же команда дважды.
 */
export function LsCta({
  label,
  onClick,
  tone = 'accent',
  size = 'md',
  withArrow,
  isWide,
  isDisabled,
}: Props) {
  return (
    <div
      className={[
        styles['ls-cta'],
        styles[`ls-cta--${size}`],
        isWide ? styles['ls-cta--is-wide'] : '',
      ].join(' ')}
    >
      <button
        className={[styles['ls-cta__label'], styles[`ls-cta__label--${tone}`]].join(' ')}
        type="button"
        disabled={isDisabled}
        onClick={onClick}
      >
        {label}
      </button>

      {withArrow && (
        <button
          className={[styles['ls-cta__arrow'], styles[`ls-cta__arrow--${tone}`]].join(' ')}
          type="button"
          disabled={isDisabled}
          onClick={onClick}
          tabIndex={-1}
          aria-hidden="true"
        >
          <span className={styles['ls-cta__icon']} />
        </button>
      )}
    </div>
  );
}
